import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';
import { ImageUploader } from '../../components/ImageUploader';

interface Profile {
  id: string;
  name: string;
  email: string | null;
  emailVerified: boolean;
  phone: string | null;
  phoneVerified: boolean;
  role: string;
  verified: boolean;
  verificationTier: string | null;
  status: string;
  image: string | null;
  city: string | null;
  bio: string | null;
  walletBalance: string | number;
  trustedBidder: boolean;
  lastLoginAt: string | null;
  totalLogins: number;
  createdAt: string;
  pendingName: string | null;
  pendingEmail: string | null;
  pendingPhone: string | null;
  pendingNameAt: string | null;
  nameRejectReason: string | null;
  nameRejectAt: string | null;
}

type ChangeKind = 'name' | 'email' | 'phone' | null;

export function ProfilePage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: () => api.get<ApiSuccess<Profile>>('/api/me/profile'),
  });

  const [form, setForm] = useState({ city: '', bio: '', image: '' });
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [openChange, setOpenChange] = useState<ChangeKind>(null);

  useEffect(() => {
    if (data?.data) {
      const p = data.data;
      setForm({ city: p.city ?? '', bio: p.bio ?? '', image: p.image ?? '' });
    }
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: () => api.put('/api/me/profile', form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['me', 'profile'] }); setSavedAt(new Date()); },
  });

  const cancelNameMutation = useMutation({
    mutationFn: () => api.post('/api/me/profile/cancel-name-change', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'profile'] }),
  });

  if (isLoading || !data?.data) return <div className="text-gray-500">⟳ جاري التحميل…</div>;
  const p = data.data;

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-6">الملف الشخصي</h1>

      {/* بطاقة معلومات سريعة */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex items-center gap-5 flex-wrap">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-navy to-navy-dark flex items-center justify-center text-white text-3xl shrink-0">
              {p.image ? <img src={p.image} alt="" className="w-full h-full rounded-full object-cover" /> : '👤'}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-black text-navy-dark">{p.name}</h2>
              <p className="text-gray-500 text-sm">{p.email}</p>
              <div className="flex gap-1 mt-2 flex-wrap">
                <Badge color={p.role.includes('ADMIN') ? 'gold' : 'navy'}>{p.role}</Badge>
                {p.verificationTier && <Badge color="green">💎 {p.verificationTier}</Badge>}
                {p.emailVerified && <Badge color="green">✓ إيميل موثّق</Badge>}
                {p.phoneVerified && <Badge color="green">✓ جوال موثّق</Badge>}
                {p.trustedBidder && <Badge color="gold">⭐ مزايد موثوق</Badge>}
              </div>
            </div>
            <div className="text-left text-xs text-gray-500 shrink-0 space-y-1">
              <div>عضو منذ: {new Date(p.createdAt).toLocaleDateString('ar-SA')}</div>
              {p.lastLoginAt && <div>آخر دخول: {new Date(p.lastLoginAt).toLocaleString('ar-SA')}</div>}
              <div>عدد مرات الدخول: {p.totalLogins}</div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* 🪪 رخصة فال (مطلوبة لنشر العقارات) */}
      <FalLicenseCard />

      {/* 🔐 المعلومات الحساسة (محميّة) */}
      <Card className="mb-6">
        <CardBody>
          <h2 className="font-black text-lg text-navy-dark mb-1 border-b-2 border-gold pb-2 inline-block">
            🔐 المعلومات الموثّقة
          </h2>
          <p className="text-xs text-gray-500 mb-4">
            تعديل هذه الحقول مقيّد لحماية حسابك. الاسم يحتاج موافقة الإدارة، والإيميل والجوال يحتاج كود تأكيد على القناة الحالية.
          </p>

          <div className="space-y-3">
            {/* الاسم */}
            <div className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-xl bg-gray-50 border border-gray-200">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 mb-0.5">الاسم</div>
                <div className="font-bold text-navy-dark">{p.name}</div>
                {p.pendingName && (
                  <div className="text-xs text-amber-700 mt-1">
                    ⏳ بانتظار موافقة الإدارة على: <b>{p.pendingName}</b>
                  </div>
                )}
                {/* 🛑 آخر سبب رفض (يظهر فقط لو ما فيه طلب جاري) */}
                {!p.pendingName && p.nameRejectReason && (
                  <div className="text-xs text-red-700 mt-1.5 p-2 rounded-lg bg-red-50 border border-red-200">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span>❌</span>
                      <b>تم رفض آخر طلب تغيير اسم</b>
                      {p.nameRejectAt && (
                        <span className="text-[10px] text-red-500">
                          · {new Date(p.nameRejectAt).toLocaleDateString('ar-SA')}
                        </span>
                      )}
                    </div>
                    <div className="text-red-600">السبب: {p.nameRejectReason}</div>
                  </div>
                )}
              </div>
              {p.pendingName ? (
                <Button variant="ghost" onClick={() => cancelNameMutation.mutate()} loading={cancelNameMutation.isPending}>
                  إلغاء الطلب
                </Button>
              ) : (
                <Button variant="ghost" onClick={() => setOpenChange('name')}>طلب تغيير</Button>
              )}
            </div>

            {/* الإيميل */}
            <div className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-xl bg-gray-50 border border-gray-200">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 mb-0.5">الإيميل</div>
                <div className="font-bold text-navy-dark" dir="ltr">{p.email ?? '—'}</div>
                {p.pendingEmail && (
                  <div className="text-xs text-amber-700 mt-1" dir="ltr">
                    ⏳ بانتظار التأكيد لـ: <b>{p.pendingEmail}</b>
                  </div>
                )}
              </div>
              <Button variant="ghost" onClick={() => setOpenChange('email')}>تغيير الإيميل</Button>
            </div>

            {/* الجوال */}
            <div className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-xl bg-gray-50 border border-gray-200">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 mb-0.5">الجوال</div>
                <div className="font-bold text-navy-dark" dir="ltr">{p.phone ?? '—'}</div>
                {p.pendingPhone && (
                  <div className="text-xs text-amber-700 mt-1" dir="ltr">
                    ⏳ بانتظار التأكيد لـ: <b>{p.pendingPhone}</b>
                  </div>
                )}
              </div>
              <Button variant="ghost" onClick={() => setOpenChange('phone')}>تغيير الجوال</Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* تعديل البيانات الحرّة */}
      <Card>
        <CardBody>
          <h2 className="font-black text-lg text-navy-dark mb-4 border-b-2 border-gold pb-2 inline-block">تعديل البيانات</h2>
          <div className="mb-5">
            <ImageUploader
              value={form.image}
              onChange={(url) => setForm({ ...form, image: url ?? '' })}
              purpose="PROFILE_IMAGE"
              label="📷 صورتي الشخصية"
              hint="JPG/PNG/WebP — حد أقصى 2MB"
              shape="circle"
              size="lg"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="المدينة" value={form.city}  onChange={(e) => setForm({ ...form, city:  e.target.value })} />
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-ink mb-1">نبذة عنك</label>
              <textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            {savedAt && <span className="text-xs text-green-600 self-center">✓ حُفظ ({savedAt.toLocaleTimeString('ar-SA')})</span>}
            <Button variant="primary" onClick={() => updateMutation.mutate()} loading={updateMutation.isPending}>💾 حفظ التغييرات</Button>
          </div>
        </CardBody>
      </Card>

      {/* 🔄 مودالات التغيير المقيّد */}
      {openChange === 'name'  && <NameChangeModal  current={p.name} onClose={() => setOpenChange(null)} />}
      {openChange === 'email' && <EmailChangeModal current={p.email ?? ''} onClose={() => setOpenChange(null)} />}
      {openChange === 'phone' && <PhoneChangeModal current={p.phone ?? ''} onClose={() => setOpenChange(null)} />}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━ Modals ━━━━━━━━━━━━━━━━━━━━━━

function ModalShell({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-black text-navy-dark">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function NameChangeModal({ current, onClose }: { current: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState(current);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');

  const m = useMutation({
    mutationFn: () => api.post<ApiSuccess>('/api/me/profile/request-name-change', { newName }),
    onSuccess: (r) => {
      setDone(r.message ?? 'تم إرسال الطلب');
      setErr('');
      qc.invalidateQueries({ queryKey: ['me', 'profile'] });
      setTimeout(onClose, 1500);
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <ModalShell title="📝 طلب تغيير الاسم" onClose={onClose}>
      <p className="text-sm text-gray-600 mb-3">
        سيتم إرسال الطلب للإدارة. لن يتغيّر الاسم على حسابك حتى توافق الإدارة.
      </p>
      <Input label="الاسم الجديد" value={newName} onChange={(e) => setNewName(e.target.value)} />
      {err  && <div className="text-sm text-red-600 mt-2">⛔ {err}</div>}
      {done && <div className="text-sm text-green-600 mt-2">✓ {done}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="ghost"   onClick={onClose}>إلغاء</Button>
        <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>إرسال الطلب</Button>
      </div>
    </ModalShell>
  );
}

function EmailChangeModal({ current, onClose }: { current: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [err, setErr] = useState('');

  const reqM = useMutation({
    mutationFn: () => api.post<ApiSuccess<{ sentTo: string; expiresInMin: number }>>('/api/me/profile/request-email-change', { newEmail }),
    onSuccess: (r) => { setSentTo(r.data?.sentTo ?? ''); setStep('confirm'); setErr(''); },
    onError: (e: Error) => setErr(e.message),
  });

  const confM = useMutation({
    mutationFn: () => api.post<ApiSuccess>('/api/me/profile/confirm-email-change', { code }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'profile'] });
      onClose();
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <ModalShell title="📧 تغيير الإيميل" onClose={onClose}>
      {step === 'request' ? (
        <>
          <p className="text-sm text-gray-600 mb-1">إيميلك الحالي:</p>
          <p className="text-sm font-bold text-navy-dark mb-3" dir="ltr">{current || '—'}</p>
          <p className="text-xs text-gray-500 mb-3">
            🔒 لحمايتك سيُرسل كود تأكيد إلى إيميلك الحالي قبل تطبيق التغيير.
          </p>
          <Input label="الإيميل الجديد" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} dir="ltr" />
          {err && <div className="text-sm text-red-600 mt-2">⛔ {err}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost"   onClick={onClose}>إلغاء</Button>
            <Button variant="primary" onClick={() => reqM.mutate()} loading={reqM.isPending}>إرسال الكود</Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-600 mb-3">
            تم إرسال كود مكوّن من 6 أرقام إلى <b dir="ltr">{sentTo}</b>. أدخله للتأكيد.
          </p>
          <Input label="الكود" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} dir="ltr" />
          {err && <div className="text-sm text-red-600 mt-2">⛔ {err}</div>}
          <div className="flex justify-between gap-2 mt-4">
            <Button variant="ghost" onClick={() => { setStep('request'); setErr(''); }}>← رجوع</Button>
            <Button variant="primary" onClick={() => confM.mutate()} loading={confM.isPending} disabled={code.length !== 6}>تأكيد التغيير</Button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function PhoneChangeModal({ current, onClose }: { current: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [newPhone, setNewPhone] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [err, setErr] = useState('');

  const reqM = useMutation({
    mutationFn: () => api.post<ApiSuccess<{ sentTo: string; expiresInMin: number }>>('/api/me/profile/request-phone-change', { newPhone }),
    onSuccess: (r) => { setSentTo(r.data?.sentTo ?? ''); setStep('confirm'); setErr(''); },
    onError: (e: Error) => setErr(e.message),
  });

  const confM = useMutation({
    mutationFn: () => api.post<ApiSuccess>('/api/me/profile/confirm-phone-change', { code }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'profile'] });
      onClose();
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <ModalShell title="📱 تغيير الجوال" onClose={onClose}>
      {step === 'request' ? (
        <>
          <p className="text-sm text-gray-600 mb-1">جوالك الحالي:</p>
          <p className="text-sm font-bold text-navy-dark mb-3" dir="ltr">{current || '—'}</p>
          <p className="text-xs text-gray-500 mb-3">
            🔒 لحمايتك سيُرسل كود (OTP) إلى جوالك الحالي قبل تطبيق التغيير.
          </p>
          <Input label="الجوال الجديد" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} dir="ltr" placeholder="+9665XXXXXXXX" />
          {err && <div className="text-sm text-red-600 mt-2">⛔ {err}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost"   onClick={onClose}>إلغاء</Button>
            <Button variant="primary" onClick={() => reqM.mutate()} loading={reqM.isPending}>إرسال الكود</Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-600 mb-3">
            تم إرسال كود مكوّن من 6 أرقام إلى <b dir="ltr">{sentTo}</b>. أدخله للتأكيد.
          </p>
          <Input label="الكود" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} dir="ltr" />
          {err && <div className="text-sm text-red-600 mt-2">⛔ {err}</div>}
          <div className="flex justify-between gap-2 mt-4">
            <Button variant="ghost" onClick={() => { setStep('request'); setErr(''); }}>← رجوع</Button>
            <Button variant="primary" onClick={() => confM.mutate()} loading={confM.isPending} disabled={code.length !== 6}>تأكيد التغيير</Button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ بطاقة رخصة فال ━━━━━━━━━━━━━━━━━━━━

interface FalLicenseData {
  falLicenseNumber: string | null;
  falLicenseExpiresAt: string | null;
  falLicenseStatus: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | null;
  falLicenseDocumentUrl: string | null;
  falLicenseUploadedAt: string | null;
  falLicenseRejectionReason: string | null;
  validity: { ok: boolean; status: string | null; reason: string | null };
}

function FalLicenseCard() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [licenseNumber, setLicenseNumber] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['me', 'fal-license'],
    queryFn: () => api.get<ApiSuccess<FalLicenseData>>('/api/me/fal-license/me'),
  });

  const submitMut = useMutation({
    mutationFn: () => api.post('/api/me/fal-license/me', {
      licenseNumber: licenseNumber.trim(),
      expiresAt: new Date(expiresAt).toISOString(),
      documentUrl: documentUrl.trim() || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fal-license'] });
      setEditing(false);
    },
  });

  if (isLoading) return null;

  const lic = data?.data;
  const status = lic?.falLicenseStatus;
  const isApproved = status === 'APPROVED' && lic?.validity?.ok;
  const isPending = status === 'PENDING';
  const isRejected = status === 'REJECTED';
  const isExpired = status === 'EXPIRED' || (status === 'APPROVED' && !lic?.validity?.ok);
  const isMissing = !lic?.falLicenseNumber;

  const startEdit = () => {
    setLicenseNumber(lic?.falLicenseNumber ?? '');
    setExpiresAt(lic?.falLicenseExpiresAt ? lic.falLicenseExpiresAt.slice(0, 10) : '');
    setDocumentUrl(lic?.falLicenseDocumentUrl ?? '');
    setEditing(true);
  };

  return (
    <Card className={`mb-6 ${
      isApproved ? 'border-green-300' :
      isPending ? 'border-amber-300' :
      isRejected || isExpired ? 'border-red-300' :
      'border-gray-200'
    }`}>
      <CardBody>
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-3">
            <div className="text-3xl">🪪</div>
            <div>
              <h2 className="font-black text-lg text-navy-dark">رخصة فال (الهيئة العامة للعقار)</h2>
              <p className="text-xs text-ink-muted">إلزامية لنشر العقارات على المنصة</p>
            </div>
          </div>
          {isApproved && <Badge color="green">✓ معتمدة وسارية</Badge>}
          {isPending && <Badge color="amber">⏳ قيد المراجعة</Badge>}
          {isRejected && <Badge color="red">✕ مرفوضة</Badge>}
          {isExpired && <Badge color="red">⌛ منتهية</Badge>}
          {isMissing && <Badge color="gray">— لم تُرفع</Badge>}
        </div>

        {!editing && lic?.falLicenseNumber && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5">
              <div className="text-[10px] text-ink-muted">رقم الرخصة</div>
              <code dir="ltr" className="text-sm font-bold text-navy-dark">{lic.falLicenseNumber}</code>
            </div>
            {lic.falLicenseExpiresAt && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5">
                <div className="text-[10px] text-ink-muted">تاريخ الانتهاء</div>
                <div className={`text-sm font-bold ${isExpired ? 'text-red-700' : 'text-navy-dark'}`}>
                  {new Date(lic.falLicenseExpiresAt).toLocaleDateString('ar-SA')}
                </div>
              </div>
            )}
          </div>
        )}

        {isMissing && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 mb-3">
            ⚠️ <b>لم ترفع رخصة فال بعد.</b> لنشر أي عقار على المنصة، يجب أن تكون لديك رخصة فال سارية ومعتمدة من الهيئة العامة للعقار.
          </div>
        )}
        {isPending && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 mb-3">
            ⏳ <b>رخصتك قيد المراجعة.</b> سيتم إعلامك بالنتيجة عبر الإيميل والإشعارات قريباً.
          </div>
        )}
        {isRejected && lic?.falLicenseRejectionReason && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-800 mb-3">
            <b>✕ تم رفض الرخصة. السبب:</b><br/>
            {lic.falLicenseRejectionReason}
          </div>
        )}
        {isExpired && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-800 mb-3">
            ⌛ <b>رخصتك منتهية الصلاحية.</b> يرجى تجديدها من الهيئة وتحديث البيانات هنا.
          </div>
        )}

        {editing ? (
          <div className="space-y-3 border-t pt-3">
            <Input
              label="رقم رخصة فال *"
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              placeholder="مثل: FAL-1234567890"
              dir="ltr"
              hint="من بوابة الهيئة العامة للعقار (5 أحرف فأكثر)"
            />
            <Input
              label="تاريخ انتهاء الرخصة *"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              hint="حسب تاريخ الانتهاء على البوابة الرسمية"
            />
            <Input
              label="رابط مستند الرخصة (اختياري)"
              value={documentUrl}
              onChange={(e) => setDocumentUrl(e.target.value)}
              placeholder="https://..."
              dir="ltr"
              hint="ارفع الملف على أي خدمة سحابية وضع الرابط هنا"
            />
            {submitMut.isError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
                ⚠️ {(submitMut.error as Error).message}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setEditing(false)}>إلغاء</Button>
              <Button
                variant="primary"
                onClick={() => submitMut.mutate()}
                loading={submitMut.isPending}
                disabled={!licenseNumber.trim() || !expiresAt}
              >
                📤 إرسال للمراجعة
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-2">
            <a
              href="https://eservicesredp.rega.gov.sa/auth/queries/Brokerage"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-navy-dark hover:underline font-bold"
            >
              🔗 الذهاب لبوابة الهيئة العامة للعقار
            </a>
            <Button variant="primary" size="sm" onClick={startEdit}>
              {isMissing ? '📤 رفع رخصة فال' : '✏️ تحديث الرخصة'}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
